import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedMessage } from 'react-intl';
import {
  Card, Grid, Button, Badge,
} from '@printdeal/ui-library';
import { dispatchDataLayerProductClick } from '../../helpers/dataLayerHelpers/CategoryPageDataLayerHelper';
import { Product } from '../../types/category/category';
import ContentfulCardImage from '../ContentfulCardImage';
import type { QuickViewModalState } from './ProductsSection';
import { dispatchDataLayerEntry } from '../../helpers/dataLayerHelpers/productDataLayerHelper';

interface CategoryPageProduct {
  categoryPage: string;
  product: Product;
  productIndex: number;
  setQuickViewModal: Dispatch<SetStateAction<QuickViewModalState | null>>
  isQuickViewExp: boolean;
  lazyLoad?: boolean;
}

const MAX_COLORS = 6;

const ProductSectionCard = ({
  categoryPage, product, productIndex, setQuickViewModal, isQuickViewExp, lazyLoad = true,
}: CategoryPageProduct) => {
  const {
    path,
    image,
    description,
    productName,
    selectorAttributeValuesByName: colors,
    productReviews,
    productDiscounts,
  } = product;

  const openQuickViewModal = () => {
    dispatchDataLayerEntry(
      'show_quick_view_modal_plp',
      'click',
      {
        contentful_id: product.id,
        title: productName,
      },
    );

    setQuickViewModal({
      contentfulId: product.id,
      path: path as string,
      ...productReviews?.review_count && {
        reviews: {
          score: productReviews?.average_rating,
          totalReviews: productReviews?.review_count,
        },
      },
    });
  };

  const availableColors = useMemo(() =>
    colors?.slice(0, MAX_COLORS).filter((color) => color?.attributeValueImage),
  [colors]);

  return (
    <Grid.Item
      as={Card}
      className="pd-h-full"
      data-cy="productCard"
      link={path ? (
        <GatsbyLink
          to={path}
          onClick={() => dispatchDataLayerProductClick(categoryPage, product, productIndex)}
        />
      ) : (<> </>)}
      image={image && (
        <ContentfulCardImage
          src={image.url}
          alt={image.title}
          priority={!lazyLoad}
        />
      )}
      title={productName || ''}
      badge={product.__typename === 'ContentfulProductContent' // eslint-disable-line no-underscore-dangle
        && productDiscounts && productDiscounts.length > 0
        && (
        <Badge variant="warning">
          <FormattedMessage
            id="product.discount"
            defaultMessage="{percentage} % discount"
            values={{ percentage: productDiscounts[0].discountValue.percentage }}
          />
        </Badge>
        )}
    >
      {description && (
        <p>
          {description}
        </p>
      )}

      <footer className="pd-mt-auto pd-w-full">
        {availableColors && (
        <div className="product-card-colors pd-mb-3 pd-flex pd-flex-wrap pd-justify-start pd-gap-1 pd-text-dim-800">
          {availableColors.map((color) => (
            <img
              key={color?.attributeValueAlias || color.pimAttributeValue}
              className="pd-size-7 pd-scale-110 pd-rounded-full"
              alt={color?.attributeValueAlias || color.pimAttributeValue}
              src={`${color.attributeValueImage}?w=45&h=45&q=70&fm=png`}
            />
          ))}
          {colors && colors.length > MAX_COLORS && (
          <div className="pd-flex pd-size-7 pd-items-center pd-rounded-md pd-border-1 pd-border-solid pd-border-dim-800">
            <span className="pd-w-full pd-text-center pd-text-xs">
              +{colors.length - MAX_COLORS}
            </span>
          </div>
          )}
        </div>
        )}
        {(path && isQuickViewExp) && (
          <Button
            className="pd-relative pd-z-20 pd-ml-auto pd-mt-auto pd-hidden pd-w-full md:pd-inline-flex"
            variant="dim"
            onClick={openQuickViewModal}
          >
            <FormattedMessage id="product.card.quick.view" defaultMessage="View product information" />
          </Button>
        )}
        <Button as="span" className="pd-ml-auto pd-mt-3 pd-hidden sm:pd-flex" variant="secondary">
          <FormattedMessage id="general.compose" defaultMessage="Compose" />
        </Button>
      </footer>
    </Grid.Item>
  );
};

export default ProductSectionCard;
